import React, { useCallback } from 'react';
import { AsoScenarioScopeTargetResponse } from '@ydistri/api-sdk';
import { Column, Row } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { useDeleteAsoScopeTargetMutation, useUpdateAsoScopeTargetMutation } from '../apiAso';
import { Card } from 'antd';
import { useAsoScenarioStatus } from '../hooks/useAsoScenarioStatus';
import { AsoValidationIssueMessages } from '../validation/AsoValidationIssueMessages';
import type { AsoValidationIssue } from '../validation/asoValidationTypes';
import { getRequestErrorMessage } from '../../../lib/utils/getRequestErrorMessage';
import { useAppDispatch } from '../../../store';
import AsoScopeAssignment from './AsoScopeAssignment';
import AsoTargetControls from './AsoTargetControls';

export interface AsoTargetScopeProps {
  asoScenarioId: number;
  scopeId: number;
  target: AsoScenarioScopeTargetResponse;
  validationIssues: readonly AsoValidationIssue[];
}

const AsoTargetScope: React.FC<AsoTargetScopeProps> = ({
  asoScenarioId,
  scopeId,
  target,
  validationIssues,
}) => {
  const [deleteTarget, { isLoading: isDeleting }] = useDeleteAsoScopeTargetMutation();
  const [updateTarget, { isLoading: isAssigning }] = useUpdateAsoScopeTargetMutation();
  const { isLocked } = useAsoScenarioStatus();
  const dispatch = useAppDispatch();

  const onDelete = useCallback(async () => {
    if (isLocked) return;

    try {
      await deleteTarget({
        asoScenarioId,
        reusableScopeId: target.scopeId,
        scopeId,
        targetId: target.id,
      }).unwrap();
    } catch (error) {
      dispatch(
        addToast({
          isError: true,
          message: getRequestErrorMessage(error, 'The target priority could not be deleted.'),
        }),
      );
      throw error;
    }
  }, [asoScenarioId, deleteTarget, dispatch, isLocked, scopeId, target.id, target.scopeId]);

  const onAssignScope = useCallback(
    async (selectedScopeId: number | null) => {
      await updateTarget({
        asoScenarioId,
        scopeId,
        targetId: target.id,
        payload: { scopeId: selectedScopeId },
        previousReusableScopeId: target.scopeId,
      }).unwrap();
    },
    [asoScenarioId, scopeId, target.id, target.scopeId, updateTarget],
  );

  return (
    <AsoScopeAssignment
      asoScenarioId={asoScenarioId}
      assignmentLabel="Target"
      isAssigning={isAssigning}
      isAssignmentDisabled={isDeleting}
      onAssignScope={onAssignScope}
      selectedScopeId={target.scopeId}
    >
      {({ content, selector }) => (
        <Card
          title={
            <Row $alignItems="center" $gap="0.25rem">
              <span>{`Target ${target.priority}`}</span>
            </Row>
          }
          extra={
            <AsoTargetControls
              isDeleting={isDeleting}
              isDisabled={isAssigning || isLocked}
              onDelete={onDelete}
              priority={target.priority}
            />
          }
          type="inner"
        >
          <Column $gap="0.5rem">
            {selector}
            {content}
            <AsoValidationIssueMessages issues={validationIssues} />
          </Column>
        </Card>
      )}
    </AsoScopeAssignment>
  );
};

export default AsoTargetScope;
